<template>
  <div class="chds-loader">
    <div class="chds-loader-card">
      <!-- Loading state -->
      <template v-if="!hasError">
        <div class="chds-spinner" aria-hidden="true">
          <span class="chds-spinner-ring"></span>
          <span class="chds-spinner-emoji">🍱</span>
        </div>
        <p class="chds-eyebrow">吃荤搭素 · CHDS</p>
        <h4 class="chds-msg">{{ currentMessage }}</h4>
        <p class="chds-sub">Fresh, balanced meals — coming right up.</p>
        <div class="chds-bar"><span class="chds-bar-fill"></span></div>
        <span class="visually-hidden" role="status">Loading…</span>
      </template>

      <!-- Error state -->
      <template v-else>
        <div class="chds-error-icon" aria-hidden="true">🥢</div>
        <p class="chds-eyebrow">吃荤搭素 · CHDS</p>
        <h4 class="chds-msg">We couldn't set the table</h4>
        <p class="chds-sub">Something went wrong loading the page. Please check your connection and try again.</p>
        <div class="chds-retry">
          <button @click="$emit('retry')" class="chds-btn" :disabled="isRetrying">
            <span v-if="isRetrying" class="chds-btn-spinner" aria-hidden="true"></span>
            {{ isRetrying ? 'Trying again…' : 'Try again' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoadingScreen',
  props: {
    hasError: { type: Boolean, default: false },
    isRetrying: { type: Boolean, default: false },
  },
  emits: ['retry'],
  data() {
    return {
      messages: [
        'Warming up the wok…',
        'Balancing your plate…',
        'Cooking up something fresh…',
        'Plating high-protein goodness…',
        'Fresh, never frozen…',
        '好好吃饭 — eat properly…',
      ],
      msgIndex: 0,
      timer: null,
    };
  },
  computed: {
    currentMessage() {
      return this.messages[this.msgIndex];
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.msgIndex = (this.msgIndex + 1) % this.messages.length;
    }, 1900);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
.chds-loader {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: radial-gradient(1200px 600px at 50% -10%, #eef4e0 0%, #fbfdf6 55%, #f6f9ef 100%);
  font-family: 'Poppins', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  padding: 20px;
}

.chds-loader-card {
  position: relative;
  text-align: center;
  background: #ffffff;
  padding: 44px 40px 40px;
  border-radius: 22px;
  box-shadow: 0 18px 50px rgba(68, 105, 54, 0.14);
  max-width: 380px;
  width: 100%;
  border: 1px solid #e7eed9;
  overflow: hidden;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}
.chds-loader-card::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 5px;
  background: linear-gradient(90deg, #B6D452, #446936);
}

/* Spinner */
.chds-spinner {
  position: relative;
  width: 74px;
  height: 74px;
  margin: 6px auto 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chds-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 5px solid #eaf1dc;
  border-top-color: #446936;
  border-right-color: #86ad3f;
  animation: chds-spin 0.9s linear infinite;
}
.chds-spinner-emoji {
  font-size: 30px;
  line-height: 1;
  animation: chds-pop 1.9s ease-in-out infinite;
}

/* Text */
.chds-eyebrow {
  color: #6b8a2e;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-size: 12px;
  margin: 0 0 8px;
}
.chds-msg {
  color: #2b3a22;
  font-weight: 700;
  font-size: 19px;
  margin: 0 0 8px;
  min-height: 26px;
  animation: chds-fade 1.9s ease-in-out infinite;
}
.chds-sub {
  color: #7c8a6c;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* Progress bar */
.chds-bar {
  margin-top: 26px;
  height: 6px;
  background: #eef2e4;
  border-radius: 999px;
  overflow: hidden;
}
.chds-bar-fill {
  display: block;
  height: 100%;
  width: 40%;
  border-radius: 999px;
  background: linear-gradient(90deg, #B6D452, #446936);
  animation: chds-slide 1.5s ease-in-out infinite;
}

/* Error */
.chds-error-icon {
  font-size: 44px;
  line-height: 1;
  margin: 6px auto 18px;
}
.chds-retry { margin-top: 22px; }
.chds-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #76923b;
  color: #fff;
  border: none;
  font-weight: 700;
  font-size: 15px;
  padding: 11px 26px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s, transform 0.16s, box-shadow 0.2s;
  box-shadow: 0 8px 18px rgba(118, 146, 59, 0.26);
}
.chds-btn:hover:not(:disabled) { background: #576d27; transform: translateY(-1px); }
.chds-btn:disabled { opacity: 0.65; cursor: default; }
.chds-btn-spinner {
  width: 15px; height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: chds-spin 0.7s linear infinite;
}

@keyframes chds-spin { to { transform: rotate(360deg); } }
@keyframes chds-slide {
  0% { margin-left: -40%; }
  100% { margin-left: 100%; }
}
@keyframes chds-pop {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
}
@keyframes chds-fade {
  0%, 100% { opacity: 1; }
  8% { opacity: 0.35; }
}

@media (prefers-reduced-motion: reduce) {
  .chds-spinner-ring { animation-duration: 1.6s; }
  .chds-spinner-emoji, .chds-msg, .chds-bar-fill { animation: none; }
  .chds-bar-fill { width: 100%; }
}

@media (max-width: 480px) {
  .chds-loader-card { padding: 38px 26px 34px; }
  .chds-msg { font-size: 17px; }
}

.visually-hidden {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
